/* ============================================================================
 * Pure n' Bio — Baby Care Bundle — cart-sync logic
 * ============================================================================
 * Mirrors the food-supplement-bundle architecture: real Shopify cart via
 *   /cart.js (read), /cart/add.js (add), /cart/change.js (mutate)
 * Dispatches `cart:refresh` + `cart:updated` so the theme cart drawer stays
 * in sync. Auto-adds / removes the gift mask when threshold is crossed
 * (line property `_gift: baby-care`).
 *
 * Mode detection:
 *   - Production (Shopify):  window.Shopify object exists + hostname is
 *     not localhost. Cart hits real /cart.js endpoints.
 *   - Preview (local HTML):  no Shopify object OR hostname is localhost.
 *     Cart is mocked from in-page DOM state (`.pnb-baby-product.is-added`).
 *
 * Required config (set by the section before this script loads):
 *   window.PnbBabyCareBundleConfig = {
 *     thresholdGift:        250,
 *     thresholdShipping:    299,
 *     giftTag:             'bundle-gift-baby-care',
 *     giftLineProperty:    { key: '_gift', value: 'baby-care' },
 *     giftVariants:        { soothing: <variantId>, illuminating: <id>, energizing: <id> },
 *     productVariants:     { /* productSlug: variantId */ /* }
 *   }
 *
 * Each `.pnb-baby-product` card must carry `data-variant-id` for production.
 * In preview mode, `data-price` is enough.
 * ========================================================================== */

window.PnbBabyCareBundleConfig = window.PnbBabyCareBundleConfig || {
  thresholdGift: 250,
  thresholdShipping: 299,
  giftTag: 'bundle-gift-baby-care',
  giftLineProperty: { key: '_gift', value: 'baby-care' },
  giftVariants: { soothing: null, illuminating: null, energizing: null }
};

(function () {
  const CFG = window.PnbBabyCareBundleConfig;
  const IS_PREVIEW = !window.Shopify || location.hostname === 'localhost' || location.hostname === '127.0.0.1';
  const STATE = { items: [], total: 0, count: 0, giftSlot: null };
  const subscribers = [];

  // ---------- Preview-mode cart (DOM-driven) ----------
  function readPreviewCart() {
    const items = [];
    let total = 0;
    document.querySelectorAll('.pnb-baby-product').forEach(p => {
      if (p.classList.contains('is-added')) {
        const price = parseFloat(p.dataset.price);
        items.push({ id: p.dataset.productId || p.dataset.price, price: price, title: (p.querySelector('h3') || {}).textContent || '' });
        total += price;
      }
    });
    STATE.items = items;
    STATE.total = total;
    STATE.count = items.length;
  }

  // ---------- Production-mode cart (Shopify) ----------
  function fetchShopifyCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(r => r.json())
      .then(cart => {
        STATE.items = cart.items || [];
        STATE.total = (cart.total_price || 0) / 100;
        STATE.count = cart.item_count || 0;
      })
      .catch(() => { /* network fail — keep stale state */ });
  }
  function postShopifyAdd(variantId, properties) {
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1, properties: properties || {} })
    }).then(r => r.json());
  }
  function postShopifyChange(line, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    }).then(r => r.json());
  }

  // ---------- Gift line reconciliation ----------
  function findGiftLineIndex() {
    return STATE.items.findIndex(function (i) {
      return i.properties && i.properties[CFG.giftLineProperty.key] === CFG.giftLineProperty.value;
    });
  }
  function syncGiftLine() {
    const shouldHaveGift = STATE.total >= CFG.thresholdGift && STATE.giftSlot;
    if (IS_PREVIEW) return; // preview reflects gift state visually only
    const idx = findGiftLineIndex();
    if (shouldHaveGift && idx === -1) {
      const variantId = CFG.giftVariants[STATE.giftSlot];
      if (!variantId) return;
      const props = {};
      props[CFG.giftLineProperty.key] = CFG.giftLineProperty.value;
      return postShopifyAdd(variantId, props).then(refresh);
    } else if (!shouldHaveGift && idx >= 0) {
      return postShopifyChange(idx + 1, 0).then(refresh);
    }
  }

  // ---------- Public API ----------
  function refresh() {
    const promise = IS_PREVIEW ? Promise.resolve(readPreviewCart()) : fetchShopifyCart();
    return promise.then(function () {
      subscribers.forEach(function (fn) { fn(STATE); });
      // Dispatch theme-level events so the cart drawer / bubble re-paint
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: STATE }));
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: STATE }));
      return syncGiftLine();
    });
  }
  function add(productEl) {
    if (IS_PREVIEW) {
      productEl.classList.add('is-added');
      const btn = productEl.querySelector('[data-add]');
      btn.classList.add('is-added');
      btn.textContent = btn.dataset.addedLabel || 'Added';
      return refresh();
    }
    const variantId = productEl.dataset.variantId;
    return postShopifyAdd(variantId).then(refresh);
  }
  function remove(productEl) {
    if (IS_PREVIEW) {
      productEl.classList.remove('is-added');
      const btn = productEl.querySelector('[data-add]');
      btn.classList.remove('is-added');
      btn.textContent = btn.dataset.addLabel || 'Add';
      return refresh();
    }
    const variantId = productEl.dataset.variantId;
    const lineIdx = STATE.items.findIndex(function (i) { return String(i.variant_id) === String(variantId); });
    if (lineIdx === -1) return Promise.resolve();
    return postShopifyChange(lineIdx + 1, 0).then(refresh);
  }
  function setGiftSlot(slot) {
    STATE.giftSlot = slot;
    return refresh();
  }
  function addByVariant(variantId, properties) {
    if (IS_PREVIEW) return refresh();
    return postShopifyAdd(variantId, properties || {}).then(refresh);
  }
  function subscribe(fn) { subscribers.push(fn); }

  // Theme cart-drawer integration:
  // - We DISPATCH cart:refresh / cart:updated on our cart mutations (above) so the theme repaints.
  // - We do NOT listen to those same events here, otherwise our own dispatch creates a loop.
  //   When integrating with a theme that drives cart from elsewhere, attach a single
  //   one-way handler upstream to call PnbBabyCart.refresh() on its own internal events.

  window.PnbBabyCart = { refresh: refresh, add: add, addByVariant: addByVariant, remove: remove, setGiftSlot: setGiftSlot, subscribe: subscribe, getState: function () { return STATE; }, IS_PREVIEW: IS_PREVIEW };
})();

/* ============================================================================
 * UI bindings — re-paint widget, threshold, gift picker on every cart change
 * ========================================================================== */
(function () {
  const CFG = window.PnbBabyCareBundleConfig;
  const PnbBabyCart = window.PnbBabyCart;
  const products = document.querySelectorAll('.pnb-baby-product');

  // Threshold progress block (inside ritual section)
  const totalEl = document.querySelector('[data-cart-total-display]');
  const remainEl = document.querySelector('[data-remaining]');
  const fillEl = document.querySelector('[data-progress-fill]');
  const msgEl = document.querySelector('.pnb-baby-progress-msg');
  const detailEl = document.querySelector('.pnb-baby-progress-detail');
  const msgTextLocked = (msgEl && msgEl.dataset.lockedMsg) || 'A free Born to Bio mask, just for you.';
  const msgTextUnlocked = (msgEl && msgEl.dataset.unlockedMsg) || 'Your free mask is unlocked. Pick yours below.';
  const detailTextUnlocked = (detailEl && detailEl.dataset.unlockedDetail) || 'You’ll choose your Born to Bio face mask at checkout.';
  const detailTextLockedTemplate = (detailEl && detailEl.dataset.lockedDetailTemplate) || 'Add __REMAINING__ SAR more to unlock your free mask.';

  // Gift picker lock + cards
  const giftLock = document.querySelector('[data-gift-lock]');
  const giftRemainEl = document.querySelector('[data-gift-remaining]');
  const giftCards = document.querySelectorAll('[data-gift]');

  // Floating widget
  const widget = document.querySelector('[data-widget]');
  const widgetTotalEl = document.querySelector('[data-widget-total]');
  const giftRow = document.querySelector('.pnb-baby-widget-row[data-goal="gift"]');
  const shipRow = document.querySelector('.pnb-baby-widget-row[data-goal="shipping"]');
  const giftTextEl = document.querySelector('[data-goal-text="gift"]');
  const shipTextEl = document.querySelector('[data-goal-text="shipping"]');
  const giftTextTemplateLocked = (giftTextEl && giftTextEl.dataset.lockedTemplate) || '<b>__REMAINING__ SAR</b>&nbsp;to free mask';
  const giftTextUnlocked = (giftTextEl && giftTextEl.dataset.unlockedText) || 'Free mask unlocked';
  const shipTextTemplateLocked = (shipTextEl && shipTextEl.dataset.lockedTemplate) || '<b>__REMAINING__ SAR</b>&nbsp;to free shipping';
  const shipTextUnlocked = (shipTextEl && shipTextEl.dataset.unlockedText) || 'Free shipping unlocked';

  function paint(state) {
    const total = state.total;
    const count = state.count;
    const giftRemain = Math.max(0, CFG.thresholdGift - total);
    const shipRemain = Math.max(0, CFG.thresholdShipping - total);

    // Threshold block (in ritual section)
    if (totalEl) totalEl.textContent = total;
    if (fillEl) fillEl.style.width = Math.min(100, (total / CFG.thresholdGift) * 100) + '%';
    if (msgEl && detailEl) {
      if (giftRemain > 0) {
        if (remainEl) remainEl.textContent = giftRemain;
        msgEl.textContent = msgTextLocked;
        detailEl.innerHTML = detailTextLockedTemplate.replace('__REMAINING__', '<span data-remaining>' + giftRemain + '</span>');
      } else {
        msgEl.textContent = msgTextUnlocked;
        detailEl.textContent = detailTextUnlocked;
      }
    }

    // Gift picker lock
    if (giftLock) {
      if (giftRemainEl) giftRemainEl.textContent = giftRemain;
      giftLock.classList.toggle('is-unlocked', giftRemain === 0);
    }

    // Floating widget
    if (widget) {
      widgetTotalEl.textContent = total;
      // Gift goal row
      if (giftRemain > 0) {
        giftTextEl.innerHTML = giftTextTemplateLocked.replace('__REMAINING__', giftRemain);
        giftRow.classList.remove('is-unlocked');
      } else {
        giftTextEl.innerHTML = giftTextUnlocked;
        giftRow.classList.add('is-unlocked');
      }
      // Shipping goal row
      if (shipRemain > 0) {
        shipTextEl.innerHTML = shipTextTemplateLocked.replace('__REMAINING__', shipRemain);
        shipRow.classList.remove('is-unlocked');
      } else {
        shipTextEl.innerHTML = shipTextUnlocked;
        shipRow.classList.add('is-unlocked');
      }
      // Always show the widget so the rewards (free shipping / free mask) are visible from the start
      widget.classList.add('is-visible');
      document.body.classList.add('pnb-baby-has-sticky');
    }
  }

  PnbBabyCart.subscribe(paint);

  // ADD buttons on product cards → CartManager.add/remove
  products.forEach(function (p) {
    const btn = p.querySelector('[data-add]');
    if (!btn) return;
    btn.addEventListener('click', function () {
      if (p.classList.contains('is-added')) PnbBabyCart.remove(p);
      else PnbBabyCart.add(p);
    });
  });

  // Widget toggle (collapse/expand)
  const widgetToggle = document.querySelector('[data-widget-toggle]');
  if (widgetToggle && widget) {
    widgetToggle.addEventListener('click', function () { widget.classList.toggle('is-collapsed'); });
  }
  // Widget CTA: preview → scroll to ritual; production → /cart
  const widgetCta = document.querySelector('[data-widget-cta]');
  if (widgetCta) {
    widgetCta.addEventListener('click', function () {
      if (PnbBabyCart.IS_PREVIEW) {
        const target = document.getElementById('ritual');
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.location.href = '/cart';
      }
    });
  }

  // Gift picker — single-pick → CartManager.setGiftSlot
  giftCards.forEach(function (card) {
    const pickBtn = card.querySelector('[data-gift-pick]');
    if (!pickBtn) return;
    const pickedLabel = pickBtn.dataset.pickedLabel || 'Picked';
    const pickLabel = pickBtn.dataset.pickLabel || 'Pick this';
    pickBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      const wasPicked = card.classList.contains('is-picked');
      giftCards.forEach(function (c) {
        c.classList.remove('is-picked');
        c.querySelector('[data-gift-pick]').textContent = pickLabel;
      });
      if (wasPicked) {
        PnbBabyCart.setGiftSlot(null);
      } else {
        card.classList.add('is-picked');
        pickBtn.textContent = pickedLabel;
        PnbBabyCart.setGiftSlot(card.dataset.gift);
      }
    });
  });

  // Testimonials carousel — native scroll-snap + prev/next arrows (RTL-aware).
  // Touch/trackpad scrolls naturally; arrows nudge the track by one card width.
  (function () {
    const track = document.querySelector('[data-test-track]');
    const prev = document.querySelector('[data-test-prev]');
    const next = document.querySelector('[data-test-next]');
    if (!track || !prev || !next) return;
    const isRtl = getComputedStyle(track).direction === 'rtl';
    function step() {
      const card = track.querySelector('.pnb-baby-testimonial');
      if (!card) return 320;
      const gap = parseFloat(getComputedStyle(track).gap) || 16;
      return card.getBoundingClientRect().width + gap;
    }
    function updateBounds() {
      // In RTL, scrollLeft is negative (or positive depending on browser); rely on max/min calc.
      const max = track.scrollWidth - track.clientWidth - 1;
      const sl = Math.abs(track.scrollLeft);
      const atStart = sl <= 1;
      const atEnd = sl >= max;
      // "prev" = earlier in reading order, "next" = later — flip in RTL
      const startBtn = isRtl ? next : prev;
      const endBtn = isRtl ? prev : next;
      startBtn.setAttribute('aria-disabled', atStart ? 'true' : 'false');
      endBtn.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
    }
    function nudge(direction) {
      // direction: -1 = prev (earlier), +1 = next (later). In RTL, screen-x is mirrored.
      const dx = step() * direction * (isRtl ? -1 : 1);
      track.scrollBy({ left: dx, behavior: 'smooth' });
    }
    prev.addEventListener('click', function () { nudge(-1); });
    next.addEventListener('click', function () { nudge(1); });
    track.addEventListener('scroll', updateBounds, { passive: true });
    window.addEventListener('resize', updateBounds);
    updateBounds();
  })();

  // FAQ accordion — single-open behaviour (independent of cart)
  const faqItems = document.querySelectorAll('.pnb-baby-faq-item');
  faqItems.forEach(function (item) {
    const q = item.querySelector('[data-faq-q]');
    if (!q) return;
    q.addEventListener('click', function () {
      const wasOpen = item.classList.contains('is-open');
      faqItems.forEach(function (i) { i.classList.remove('is-open'); });
      if (!wasOpen) item.classList.add('is-open');
    });
  });

  // Initial paint on page load (handles real cart contents on Shopify too)
  PnbBabyCart.refresh();

  /* ============================================================================
   * Quick-view modal — opens when a product or gift card is clicked.
   * Fetches /products/{handle}.js (locale-prefixed for AR), populates the modal,
   * wires "Add to cart" + "Know more" → product page.
   * ========================================================================== */
  (function () {
    const modal = document.getElementById('pnb-baby-qv');
    if (!modal) return;

    const root = document.querySelector('.baby-care-bundle');
    const IS_RTL = root && root.getAttribute('dir') === 'rtl';
    const LOCALE_PREFIX = IS_RTL ? '/ar' : '';
    const LABELS = {
      add: modal.dataset.labelAdd || 'Add to cart',
      added: modal.dataset.labelAdded || '✓ Added',
      loading: modal.dataset.labelLoading || 'Loading…',
      inStock: modal.dataset.labelInStock || 'In stock',
      outOfStock: modal.dataset.labelOutOfStock || 'Sold out',
      failed: modal.dataset.labelFailed || 'Could not load'
    };
    const SAR_HTML = '<span class="pnb-baby-sar" aria-label="' + (IS_RTL ? 'ريال' : 'SAR') + '"></span>';
    const TABBY_LOGO_HTML = (document.querySelector('.pnb-baby-tabby-logo') || {}).outerHTML || '';
    const QV_CACHE = {};
    const QV_STATE = { images: [], index: 0, product: null };

    function $id(id) { return document.getElementById(id); }

    function open() {
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('pnb-baby-qv-open');
    }
    function close() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('pnb-baby-qv-open');
    }

    function renderCarousel() {
      const track = $id('pnb-baby-qv-track');
      const dots = $id('pnb-baby-qv-dots');
      if (!track || !dots) return;
      track.innerHTML = '';
      dots.innerHTML = '';
      QV_STATE.images.forEach(function (src, i) {
        const slide = document.createElement('div');
        slide.className = 'pnb-baby-qv-slide';
        const img = document.createElement('img');
        img.src = src;
        img.loading = 'lazy';
        img.alt = '';
        slide.appendChild(img);
        track.appendChild(slide);

        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'pnb-baby-qv-dot';
        dot.setAttribute('data-i', String(i));
        dot.setAttribute('aria-label', String(i + 1));
        dots.appendChild(dot);
      });
      goTo(0);
    }

    function goTo(i) {
      if (!QV_STATE.images.length) return;
      if (i < 0) i = QV_STATE.images.length - 1;
      if (i >= QV_STATE.images.length) i = 0;
      QV_STATE.index = i;
      const track = $id('pnb-baby-qv-track');
      if (track) {
        const offset = IS_RTL ? (i * 100) : (-i * 100);
        track.style.transform = 'translateX(' + offset + '%)';
      }
      const dots = document.querySelectorAll('#pnb-baby-qv-dots .pnb-baby-qv-dot');
      dots.forEach(function (d, idx) { d.classList.toggle('is-active', idx === i); });
    }

    function populate(p) {
      const brandEl = $id('pnb-baby-qv-brand');
      const titleEl = $id('pnb-baby-qv-title');
      const priceEl = $id('pnb-baby-qv-price');
      const tabbyEl = $id('pnb-baby-qv-tabby');
      const stockEl = $id('pnb-baby-qv-stock');
      const descEl = $id('pnb-baby-qv-desc');
      const addBtn = $id('pnb-baby-qv-add');
      const moreLink = $id('pnb-baby-qv-more');

      if (brandEl) brandEl.textContent = p.vendor || '';
      if (titleEl) titleEl.textContent = p.title || '';

      const v = (p.variants && p.variants[0]) || {};
      const priceCents = (v.price || p.price || 0);
      const priceNum = Math.round(priceCents / 100);
      if (priceEl) priceEl.innerHTML = '<b>' + priceNum + '</b> ' + SAR_HTML;

      if (tabbyEl) {
        const splitNum = Math.round(priceCents / 4) / 100;
        const splitStr = (splitNum % 1 === 0) ? String(splitNum) : splitNum.toFixed(2);
        tabbyEl.innerHTML = TABBY_LOGO_HTML + ' 4 × <b>' + splitStr + '</b> ' + SAR_HTML;
      }

      const available = !!v.available;
      if (stockEl) {
        stockEl.textContent = available ? LABELS.inStock : LABELS.outOfStock;
        stockEl.classList.toggle('is-soldout', !available);
      }

      if (descEl) {
        const tmp = document.createElement('div');
        tmp.innerHTML = p.description || '';
        let text = (tmp.textContent || '').replace(/\s+/g, ' ').trim();
        if (text.length > 600) text = text.substring(0, 600).replace(/\s\S*$/, '') + '…';
        descEl.textContent = text;
      }

      QV_STATE.images = (p.images || []).map(function (s) {
        if (!s) return s;
        return s.indexOf('//') === 0 ? 'https:' + s : s;
      });
      if (!QV_STATE.images.length && p.featured_image) QV_STATE.images = [p.featured_image];
      QV_STATE.product = p;
      renderCarousel();

      if (addBtn) {
        const hasVariant = !!v.id;
        addBtn.style.display = hasVariant ? '' : 'none';
        addBtn.disabled = !available || !hasVariant;
        addBtn.classList.toggle('is-disabled', !available);
        addBtn.classList.remove('is-added');
        addBtn.textContent = available ? LABELS.add : LABELS.outOfStock;
        addBtn.dataset.handle = p.handle || '';
        addBtn.dataset.variantId = v.id || '';
        addBtn.dataset.price = priceNum;
      }
      if (moreLink && p.handle) {
        moreLink.href = LOCALE_PREFIX + '/products/' + p.handle;
      }
    }

    function showLoading() {
      const titleEl = $id('pnb-baby-qv-title');
      const priceEl = $id('pnb-baby-qv-price');
      const stockEl = $id('pnb-baby-qv-stock');
      const descEl = $id('pnb-baby-qv-desc');
      const tabbyEl = $id('pnb-baby-qv-tabby');
      const brandEl = $id('pnb-baby-qv-brand');
      const track = $id('pnb-baby-qv-track');
      if (brandEl) brandEl.textContent = '';
      if (titleEl) titleEl.textContent = LABELS.loading;
      if (priceEl) priceEl.textContent = '';
      if (tabbyEl) tabbyEl.textContent = '';
      if (stockEl) { stockEl.textContent = ''; stockEl.classList.remove('is-soldout'); }
      if (descEl) descEl.textContent = '';
      if (track) track.innerHTML = '';
    }

    function load(handle) {
      if (!handle) return;
      showLoading();
      open();
      if (QV_CACHE[handle]) { populate(QV_CACHE[handle]); return; }
      fetch(LOCALE_PREFIX + '/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
        .then(function (p) { QV_CACHE[handle] = p; populate(p); })
        .catch(function () {
          const titleEl = $id('pnb-baby-qv-title');
          if (titleEl) titleEl.textContent = LABELS.failed;
        });
    }

    // Card-click handlers — exclude clicks on existing buttons
    document.addEventListener('click', function (e) {
      // Quick-view triggers
      if (e.target.closest('button, a') === null) {
        const productCard = e.target.closest('.pnb-baby-product[data-handle]');
        if (productCard) { load(productCard.getAttribute('data-handle')); return; }
        const giftCard = e.target.closest('.pnb-baby-gift-card[data-handle]');
        if (giftCard) { load(giftCard.getAttribute('data-handle')); return; }
      }
      // Modal close
      if (e.target.closest('.pnb-baby-qv-close') || e.target.classList.contains('pnb-baby-qv-backdrop')) {
        close(); return;
      }
      if (e.target.closest('.pnb-baby-qv-prev')) { goTo(QV_STATE.index - 1); return; }
      if (e.target.closest('.pnb-baby-qv-next')) { goTo(QV_STATE.index + 1); return; }
      const dot = e.target.closest('.pnb-baby-qv-dot');
      if (dot) { goTo(parseInt(dot.getAttribute('data-i'), 10)); return; }
      // Add-to-cart from inside modal
      const addBtn = e.target.closest('#pnb-baby-qv-add');
      if (addBtn && !addBtn.disabled) {
        const variantId = Number(addBtn.dataset.variantId || 0);
        if (!variantId) return;
        const orig = addBtn.textContent;
        addBtn.disabled = true;
        PnbBabyCart.addByVariant(variantId, {})
          .then(function () {
            addBtn.textContent = LABELS.added;
            addBtn.classList.add('is-added');
            // Reflect on the corresponding card
            const handle = addBtn.dataset.handle;
            if (handle) {
              const card = document.querySelector('.pnb-baby-product[data-handle="' + handle + '"]');
              if (card) {
                card.classList.add('is-added');
                const cardBtn = card.querySelector('[data-add]');
                if (cardBtn) {
                  cardBtn.classList.add('is-added');
                  cardBtn.textContent = cardBtn.dataset.addedLabel || cardBtn.textContent;
                }
              }
            }
          })
          .catch(function () {
            addBtn.textContent = IS_RTL ? '⚠ تعذّر الإضافة' : '⚠ Could not add';
          })
          .then(function () {
            setTimeout(function () { close(); addBtn.disabled = false; addBtn.textContent = orig; addBtn.classList.remove('is-added'); }, 900);
          });
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) close();
    });

    // Set sessionStorage flags so the back-to-bundle banner can show on product pages.
    // Most-recent-visit wins: clear any other bundle's active flags so only baby's
    // banner is rendered on subsequent product pages this session, and reset our
    // own dismiss flag so a re-visit re-shows the banner.
    try {
      sessionStorage.removeItem('pnbf_food_bundle_active');
      sessionStorage.removeItem('pnbf_food_bundle_path');
      sessionStorage.removeItem('pnbf_btb_dismissed');
      sessionStorage.removeItem('pnb_baby_btb_dismissed');
      sessionStorage.setItem('pnb_baby_bundle_active', '1');
      sessionStorage.setItem('pnb_baby_bundle_path', LOCALE_PREFIX + '/pages/baby-care-bundle');
    } catch (err) { /* private browsing — no-op */ }
  })();
})();
